import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  collectionGroup,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  increment,
  runTransaction,
  writeBatch,
  serverTimestamp,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { db } from './config';
import type { JSONContent } from '@tiptap/core';

// ── Posts ─────────────────────────────────────────────────────────────────────

export type BlogPostStatus = 'draft' | 'scheduled' | 'published' | 'archived';

export interface BlogPostDocument {
  id?: string;
  slug: string;
  title: string;
  excerpt: string;
  contentJson: JSONContent;
  coverImageUrl?: string;
  /** Small (~320px) compressed thumbnail used by list/card views (home,
   * category, /news) so they don't have to fetch the full-size cover image
   * embedded in every post document just to paint a thumbnail. Falls back to
   * coverImageUrl for posts saved before this field existed. */
  coverImageThumbUrl?: string;
  categoryId: string;
  categoryName: string;
  categorySlug: string;
  tagIds: string[];
  tagNames: string[];
  status: BlogPostStatus;
  featured: boolean;
  publishedAt: string | null;
  scheduledFor: string | null;
  authorId: string;
  authorName: string;
  authorAvatarUrl?: string;
  seoTitle?: string;
  seoDescription?: string;
  ogImageUrl?: string;
  readingTimeMinutes: number;
  likeCount: number;
  commentCount: number;
  viewCount: number;
  shareCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PostPage {
  posts: BlogPostDocument[];
  // The last post's publishedAt — a plain, serializable value-based cursor
  // (Firestore's startAfter accepts either a snapshot or the raw ordered-by
  // field value) rather than a QueryDocumentSnapshot. That's what lets a
  // server component's initial fetch (admin SDK, its own snapshot type) hand
  // off cleanly to this same client function for subsequent "load more"
  // pages, with no snapshot object ever crossing the server/client boundary.
  nextCursor?: string;
}

function toPost(d: QueryDocumentSnapshot<DocumentData>): BlogPostDocument {
  return { id: d.id, ...d.data() } as BlogPostDocument;
}

/** The Firestore client SDK (unlike the Admin SDK) throws on any field value
 * of `undefined` — e.g. an optional field like coverImageUrl/authorAvatarUrl
 * that was never set. Strip those keys out rather than requiring every
 * caller to remember to. */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const key in obj) {
    if (obj[key] !== undefined) out[key] = obj[key];
  }
  return out;
}

export async function createPost(data: Omit<BlogPostDocument, 'id'>): Promise<string> {
  const ref = await addDoc(collection(db, 'posts'), stripUndefined(data));
  return ref.id;
}

export async function updatePost(postId: string, updates: Partial<BlogPostDocument>): Promise<void> {
  await updateDoc(doc(db, 'posts', postId), stripUndefined({ ...updates, updatedAt: new Date().toISOString() }));
}

export async function deletePost(postId: string): Promise<void> {
  await deleteDoc(doc(db, 'posts', postId));
}

export async function getPostById(postId: string): Promise<BlogPostDocument | null> {
  const snap = await getDoc(doc(db, 'posts', postId));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as BlogPostDocument) : null;
}

/** Public read of a single published post by slug. Rules only allow this for status=='published' unless the caller is an admin. */
export async function getPostBySlug(slug: string): Promise<BlogPostDocument | null> {
  const q = query(collection(db, 'posts'), where('slug', '==', slug), limit(1));
  const snap = await getDocs(q);
  return snap.empty ? null : toPost(snap.docs[0]);
}

export async function getPublishedPosts(pageSize = 12, cursor?: string): Promise<PostPage> {
  const constraints = [where('status', '==', 'published'), orderBy('publishedAt', 'desc'), limit(pageSize)];
  const q = cursor
    ? query(collection(db, 'posts'), ...constraints, startAfter(cursor))
    : query(collection(db, 'posts'), ...constraints);
  const snap = await getDocs(q);
  const posts = snap.docs.map(toPost);
  return { posts, nextCursor: posts.at(-1)?.publishedAt ?? undefined };
}

export async function getFeaturedPost(): Promise<BlogPostDocument | null> {
  const q = query(
    collection(db, 'posts'),
    where('status', '==', 'published'),
    where('featured', '==', true),
    orderBy('publishedAt', 'desc'),
    limit(1)
  );
  const snap = await getDocs(q);
  return snap.empty ? null : toPost(snap.docs[0]);
}

export async function getPublishedPostsByCategory(
  categoryId: string,
  pageSize = 12,
  cursor?: string
): Promise<PostPage> {
  const constraints = [
    where('categoryId', '==', categoryId),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    limit(pageSize),
  ];
  const q = cursor
    ? query(collection(db, 'posts'), ...constraints, startAfter(cursor))
    : query(collection(db, 'posts'), ...constraints);
  const snap = await getDocs(q);
  const posts = snap.docs.map(toPost);
  return { posts, nextCursor: posts.at(-1)?.publishedAt ?? undefined };
}

export async function getRelatedPosts(post: BlogPostDocument, take = 3): Promise<BlogPostDocument[]> {
  const q = query(
    collection(db, 'posts'),
    where('categoryId', '==', post.categoryId),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    limit(take + 1)
  );
  const snap = await getDocs(q);
  return snap.docs.map(toPost).filter(p => p.id !== post.id).slice(0, take);
}

/** All posts regardless of status, for the admin posts list. Not paginated by cursor yet (admin volumes are small); capped to avoid unbounded reads. */
export async function getAllPostsForAdmin(cap = 200): Promise<BlogPostDocument[]> {
  const q = query(collection(db, 'posts'), orderBy('updatedAt', 'desc'), limit(cap));
  const snap = await getDocs(q);
  return snap.docs.map(toPost);
}

export async function incrementViewCount(postId: string): Promise<void> {
  // Best-effort, non-critical counter — no anti-fraud guarantee, matches the plan's documented tradeoff.
  // Security rules enforce this can only ever move by exactly +1 per write.
  await updateDoc(doc(db, 'posts', postId), { viewCount: increment(1) });
}

/** Called when a visitor uses any share action (copy link, native share, or a social share button) — works without authentication, matching the "sharing doesn't require login" requirement. */
export async function incrementShareCount(postId: string): Promise<void> {
  await updateDoc(doc(db, 'posts', postId), { shareCount: increment(1) });
}

/** Recomputes and persists the denormalized likeCount/commentCount snapshot used by list/card views. Called by the admin editor on save. */
/** Read-only — computes live counts without writing anything. Used by admin
 * views so they never show the stale cached likeCount/commentCount fields
 * (those only update when a post is re-saved via refreshPostCounts below). */
export async function getLivePostCounts(postId: string): Promise<{ likeCount: number; commentCount: number }> {
  const [likeSnap, commentSnap] = await Promise.all([
    getCountFromServer(collection(db, 'posts', postId, 'likes')),
    getCountFromServer(query(collection(db, 'comments'), where('postId', '==', postId), where('status', '==', 'visible'))),
  ]);
  return { likeCount: likeSnap.data().count, commentCount: commentSnap.data().count };
}

export async function refreshPostCounts(postId: string): Promise<{ likeCount: number; commentCount: number }> {
  const { likeCount, commentCount } = await getLivePostCounts(postId);
  await updateDoc(doc(db, 'posts', postId), { likeCount, commentCount });
  return { likeCount, commentCount };
}

// ── Likes ─────────────────────────────────────────────────────────────────────

export interface BlogLikeDocument {
  uid: string;
  postId: string;
  postTitle: string;
  postSlug: string;
  createdAt: string;
}

export async function getPostLikeCount(postId: string): Promise<number> {
  const snap = await getCountFromServer(collection(db, 'posts', postId, 'likes'));
  return snap.data().count;
}

export async function hasUserLikedPost(uid: string, postId: string): Promise<boolean> {
  const snap = await getDoc(doc(db, 'posts', postId, 'likes', uid));
  return snap.exists();
}

/** Atomically creates the like doc and bumps the post's denormalized
 * likeCount (what card/list views display) in the same transaction, so the
 * count visitors see never drifts from the actual like docs. The existence
 * check inside the transaction makes this idempotent — re-liking an
 * already-liked post (e.g. stale client state after a reload) is a silent
 * no-op instead of double-counting. */
/** Enforced server-side too (firestore.rules requires each write to this
 * doc's `lastAt` to equal the commit's own server timestamp — a client can't
 * forge a past/future value to dodge or extend its own cooldown), but it's
 * one shared doc per user across every post so a like/unlike toggle on any
 * post shares the same cooldown window. */
function likeRateLimitRef(uid: string) {
  return doc(db, 'likeRateLimits', uid);
}

export async function likePost(uid: string, post: BlogPostDocument): Promise<void> {
  if (!post.id) throw new Error('Post has no id');
  const likeRef = doc(db, 'posts', post.id, 'likes', uid);
  const postRef = doc(db, 'posts', post.id);
  await runTransaction(db, async tx => {
    const likeSnap = await tx.get(likeRef);
    if (likeSnap.exists()) return;
    tx.set(likeRef, {
      uid,
      postId: post.id,
      postTitle: post.title,
      postSlug: post.slug,
      createdAt: new Date().toISOString(),
    } satisfies BlogLikeDocument);
    tx.update(postRef, { likeCount: increment(1) });
    tx.set(likeRateLimitRef(uid), { lastAt: serverTimestamp() });
  });
}

export async function unlikePost(uid: string, postId: string): Promise<void> {
  const likeRef = doc(db, 'posts', postId, 'likes', uid);
  const postRef = doc(db, 'posts', postId);
  await runTransaction(db, async tx => {
    const likeSnap = await tx.get(likeRef);
    if (!likeSnap.exists()) return;
    tx.delete(likeRef);
    tx.update(postRef, { likeCount: increment(-1) });
    tx.set(likeRateLimitRef(uid), { lastAt: serverTimestamp() });
  });
}

/** All posts a user has liked, across every post, via a collectionGroup query on the denormalized `uid` field. */
export async function getUserLikedPosts(uid: string, pageSize = 24): Promise<BlogLikeDocument[]> {
  const q = query(collectionGroup(db, 'likes'), where('uid', '==', uid), orderBy('createdAt', 'desc'), limit(pageSize));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as BlogLikeDocument);
}

// ── Comments ──────────────────────────────────────────────────────────────────

export type BlogCommentStatus = 'visible' | 'hidden';

export interface BlogCommentDocument {
  id?: string;
  postId: string;
  postSlug: string;
  postTitle: string;
  authorId: string;
  authorName: string;
  authorAvatarUrl?: string;
  body: string;
  status: BlogCommentStatus;
  createdAt: string;
}

const MAX_COMMENT_LENGTH = 3000;

/** Same forgeable-timestamp protection as likeRateLimitRef — one shared
 * cooldown doc per user across every post's comments. */
function commentRateLimitRef(uid: string) {
  return doc(db, 'commentRateLimits', uid);
}

export async function createComment(data: Omit<BlogCommentDocument, 'id' | 'status' | 'createdAt'>): Promise<string> {
  const trimmed = data.body.trim();
  if (!trimmed) throw new Error('Comment cannot be empty.');
  if (trimmed.length > MAX_COMMENT_LENGTH) throw new Error('Comment is too long.');
  const commentRef = doc(collection(db, 'comments'));
  const batch = writeBatch(db);
  batch.set(commentRef, stripUndefined({
    ...data,
    body: trimmed,
    status: 'visible' as const,
    createdAt: new Date().toISOString(),
  }));
  batch.set(commentRateLimitRef(data.authorId), { lastAt: serverTimestamp() });
  await batch.commit();
  return commentRef.id;
}

export async function deleteComment(commentId: string): Promise<void> {
  await deleteDoc(doc(db, 'comments', commentId));
}

export async function setCommentStatus(commentId: string, status: BlogCommentStatus): Promise<void> {
  await updateDoc(doc(db, 'comments', commentId), { status });
}

export async function getCommentsForPost(postId: string): Promise<BlogCommentDocument[]> {
  const q = query(
    collection(db, 'comments'),
    where('postId', '==', postId),
    where('status', '==', 'visible'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BlogCommentDocument));
}

export async function getCommentsByUser(uid: string): Promise<BlogCommentDocument[]> {
  const q = query(collection(db, 'comments'), where('authorId', '==', uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BlogCommentDocument));
}

/** All comments for the admin moderation table, newest first, capped. */
export async function getAllCommentsForAdmin(cap = 300): Promise<BlogCommentDocument[]> {
  const q = query(collection(db, 'comments'), orderBy('createdAt', 'desc'), limit(cap));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BlogCommentDocument));
}

// ── Reports ───────────────────────────────────────────────────────────────────

export type BlogReportStatus = 'open' | 'resolved' | 'dismissed';

export interface BlogReportDocument {
  id?: string;
  targetType: 'post' | 'comment';
  targetId: string;
  postId: string;
  reporterId: string;
  reason: string;
  status: BlogReportStatus;
  createdAt: string;
}

export async function createReport(data: Omit<BlogReportDocument, 'id' | 'status' | 'createdAt'>): Promise<void> {
  await addDoc(collection(db, 'reports'), {
    ...data,
    status: 'open' as const,
    createdAt: new Date().toISOString(),
  });
}

export async function getOpenReports(): Promise<BlogReportDocument[]> {
  const q = query(collection(db, 'reports'), where('status', '==', 'open'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BlogReportDocument));
}

export async function setReportStatus(reportId: string, status: BlogReportStatus): Promise<void> {
  await updateDoc(doc(db, 'reports', reportId), { status });
}

// ── Categories ────────────────────────────────────────────────────────────────

export interface BlogCategoryDocument {
  id?: string;
  name: string;
  slug: string;
  description?: string;
  postCount: number;
  createdAt: string;
}

export async function createCategory(data: Omit<BlogCategoryDocument, 'id' | 'postCount' | 'createdAt'>): Promise<string> {
  const ref = await addDoc(collection(db, 'categories'), stripUndefined({
    ...data,
    postCount: 0,
    createdAt: new Date().toISOString(),
  }));
  return ref.id;
}

export async function updateCategory(categoryId: string, updates: Partial<BlogCategoryDocument>): Promise<void> {
  await updateDoc(doc(db, 'categories', categoryId), stripUndefined(updates));
}

export async function deleteCategory(categoryId: string): Promise<void> {
  await deleteDoc(doc(db, 'categories', categoryId));
}

export async function getAllCategories(): Promise<BlogCategoryDocument[]> {
  const q = query(collection(db, 'categories'), orderBy('name', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BlogCategoryDocument));
}

export async function getCategoryBySlug(slug: string): Promise<BlogCategoryDocument | null> {
  const q = query(collection(db, 'categories'), where('slug', '==', slug), limit(1));
  const snap = await getDocs(q);
  return snap.empty ? null : ({ id: snap.docs[0].id, ...snap.docs[0].data() } as BlogCategoryDocument);
}

// ── Tags ──────────────────────────────────────────────────────────────────────

export interface BlogTagDocument {
  id?: string;
  name: string;
  slug: string;
  createdAt: string;
}

export async function createTag(data: Omit<BlogTagDocument, 'id' | 'createdAt'>): Promise<string> {
  const ref = await addDoc(collection(db, 'tags'), { ...data, createdAt: new Date().toISOString() });
  return ref.id;
}

export async function deleteTag(tagId: string): Promise<void> {
  await deleteDoc(doc(db, 'tags', tagId));
}

export async function getAllTags(): Promise<BlogTagDocument[]> {
  const q = query(collection(db, 'tags'), orderBy('name', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BlogTagDocument));
}

// ── Newsletter Subscribers ──────────────────────────────────────────────────────

export interface NewsletterSubscriberDocument {
  email: string;
  createdAt: string;
}

/** Uses the email as the doc ID so re-subscribing never creates duplicates. */
export async function subscribeToNewsletter(email: string): Promise<void> {
  await setDoc(
    doc(db, 'newsletterSubscribers', email),
    { email, createdAt: new Date().toISOString() },
    { merge: true }
  );
}

export async function getAllNewsletterSubscribers(): Promise<NewsletterSubscriberDocument[]> {
  const snap = await getDocs(collection(db, 'newsletterSubscribers'));
  return snap.docs.map(d => d.data() as NewsletterSubscriberDocument);
}

export async function unsubscribeFromNewsletter(email: string): Promise<void> {
  await deleteDoc(doc(db, 'newsletterSubscribers', email));
}

// ── Contact Messages ──────────────────────────────────────────────────────────

export interface BlogContactMessageDocument {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  message?: string;
  createdAt: string;
  status: 'new' | 'read';
}

export async function createContactMessage(
  data: Omit<BlogContactMessageDocument, 'id' | 'createdAt' | 'status'>
): Promise<void> {
  await addDoc(collection(db, 'contactMessages'), stripUndefined({
    ...data,
    createdAt: new Date().toISOString(),
    status: 'new' as const,
  }));
}

export async function getAllContactMessages(): Promise<BlogContactMessageDocument[]> {
  const q = query(collection(db, 'contactMessages'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BlogContactMessageDocument));
}

export async function setContactMessageStatus(id: string, status: 'new' | 'read'): Promise<void> {
  await updateDoc(doc(db, 'contactMessages', id), { status });
}

export async function deleteContactMessage(id: string): Promise<void> {
  await deleteDoc(doc(db, 'contactMessages', id));
}

// ── Settings (singleton) ──────────────────────────────────────────────────────

export interface BlogSettingsDocument {
  siteName: string;
  tagline: string;
  defaultSeoDescription: string;
  postsPerPage: number;
  newsletterEnabled: boolean;
}

const DEFAULT_SETTINGS: BlogSettingsDocument = {
  siteName: 'Hustlance Blog',
  tagline: 'Stories, insights, and opportunities for students building their future.',
  defaultSeoDescription:
    'Hustlance Blog — stories, freelancing guides, and career advice for students building skills and their future.',
  postsPerPage: 12,
  newsletterEnabled: true,
};

export async function getBlogSettings(): Promise<BlogSettingsDocument> {
  const snap = await getDoc(doc(db, 'blogSettings', 'global'));
  return snap.exists() ? { ...DEFAULT_SETTINGS, ...(snap.data() as Partial<BlogSettingsDocument>) } : DEFAULT_SETTINGS;
}

export async function updateBlogSettings(updates: Partial<BlogSettingsDocument>): Promise<void> {
  await setDoc(doc(db, 'blogSettings', 'global'), updates, { merge: true });
}
